/**
 * MobilePromoTab - Admin page to configure the "Promote to Mobile" banner
 * shown in the web customer portal.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { Loader2, Save, BarChart3, Smartphone, Apple, PlayCircle, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DEFAULT_CFG = {
  enabled: false,
  title: 'Jetzt auch als App!',
  message: 'Alle Features in der StrongFit-App für iPhone & Android — jetzt herunterladen.',
  ios_url: '',
  android_url: '',
  show_qr: true,
  background_color: '#0B0B0F',
  primary_color: '#EAB308',
  button_label: 'App laden',
  schedule_mode: 'always',
  start_datetime: '',
  end_datetime: '',
  target_audience: 'all',
  campaign_id: '',
};

const MobilePromoTab = ({ token }) => {
  const [cfg, setCfg] = useState(DEFAULT_CFG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    (async () => {
      try {
        const [cfgRes, statsRes] = await Promise.allSettled([
          axios.get(`${API}/admin/mobile-promo/config`, { headers }),
          axios.get(`${API}/admin/mobile-promo/stats`, { headers }),
        ]);
        if (cfgRes.status === 'fulfilled') {
          setCfg({ ...DEFAULT_CFG, ...(cfgRes.value.data || {}) });
        }
        if (statsRes.status === 'fulfilled') {
          setStats(statsRes.value.data);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/mobile-promo/config`, cfg, { headers });
      toast.success('Promo-Banner gespeichert');
    } catch (e) {
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const reloadStats = async () => {
    setStatsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/mobile-promo/stats`, { headers });
      setStats(res.data);
    } catch (e) {
      toast.error('Statistik konnte nicht geladen werden');
    } finally {
      setStatsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-gray-500 p-6">
        <Loader2 className="w-4 h-4 animate-spin" /> Lade Promo-Banner…
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-yellow-500/20 border border-yellow-500/40 flex items-center justify-center">
          <Smartphone className="text-yellow-600" size={22} />
        </div>
        <div className="flex-1">
          <h2 className="text-xl font-bold text-gray-900">Mobile App Promo-Banner</h2>
          <p className="text-sm text-gray-500 mt-1">
            Bestehende Web-Portal-Kunden auf die neue Flutter-App aufmerksam machen.
            QR-Code für Desktop-User, direkte Store-Links für Mobile-User, Klick-Tracking.
          </p>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={cfg.enabled}
            onChange={(e) => set('enabled', e.target.checked)}
            data-testid="promo-enable-toggle"
            className="w-5 h-5 accent-yellow-500"
          />
          <span className="text-sm text-gray-900 font-semibold">Aktiv</span>
        </label>
      </div>

      {/* Stats Card */}
      {stats && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <BarChart3 className="text-yellow-600" size={18} />
              <h3 className="text-sm font-semibold text-gray-900">Kampagnen-Statistik</h3>
            </div>
            <button
              onClick={reloadStats}
              disabled={statsLoading}
              className="text-xs text-gray-500 hover:text-gray-900"
            >
              {statsLoading ? 'Lade…' : 'Aktualisieren'}
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-center">
            <StatCard value={stats.views} label="Views" color="#EAB308" />
            <StatCard value={stats.clicks_ios} label="iOS Klicks" color="#60A5FA" />
            <StatCard value={stats.clicks_android} label="Android Klicks" color="#34D399" />
            <StatCard value={`${stats.ctr_percent}%`} label="CTR" color="#F472B6" />
            <StatCard value={stats.dismissals} label="Geschlossen" color="#F87171" />
          </div>
          {/* Conversion-KPIs — der ultimative Indikator: wieviele User haben
              tatsächlich die App installiert nachdem sie den Banner gesehen
              haben. `installs` zählt eindeutige Kunden mit
              `mobile_app_first_login` >= Kampagnen-Start. */}
          <div className="grid grid-cols-3 gap-3 text-center mt-4 pt-4 border-t border-gray-200">
            <StatCard value={stats.installs ?? 0} label="App-Installs" color="#22D3EE" />
            <StatCard
              value={`${stats.conversion_rate_percent ?? 0}%`}
              label="Conversion (Views→Install)"
              color="#A78BFA"
            />
            <StatCard
              value={`${stats.click_to_install_percent ?? 0}%`}
              label="Click→Install"
              color="#FB923C"
            />
          </div>
        </div>
      )}

      {/* Content Config */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 space-y-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Inhalt</h3>

        <Field label="Titel">
          <input
            type="text"
            value={cfg.title}
            onChange={(e) => set('title', e.target.value)}
            data-testid="promo-title"
            className="w-full bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900"
          />
        </Field>

        <Field label="Nachricht">
          <textarea
            value={cfg.message}
            onChange={(e) => set('message', e.target.value)}
            data-testid="promo-message"
            rows={2}
            className="w-full bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 resize-none"
          />
        </Field>

        <Field label="Button-Label">
          <input
            type="text"
            value={cfg.button_label}
            onChange={(e) => set('button_label', e.target.value)}
            data-testid="promo-button-label"
            className="w-full bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900"
          />
        </Field>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label={<><Apple size={12} className="inline mr-1" /> iOS App Store URL</>}>
            <input
              type="url"
              value={cfg.ios_url || ''}
              onChange={(e) => set('ios_url', e.target.value)}
              placeholder="https://apps.apple.com/app/id..."
              data-testid="promo-ios-url"
              className="w-full bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900"
            />
          </Field>
          <Field label={<><PlayCircle size={12} className="inline mr-1" /> Google Play URL</>}>
            <input
              type="url"
              value={cfg.android_url || ''}
              onChange={(e) => set('android_url', e.target.value)}
              placeholder="https://play.google.com/store/apps/details?id=..."
              data-testid="promo-android-url"
              className="w-full bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900"
            />
          </Field>
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={cfg.show_qr}
            onChange={(e) => set('show_qr', e.target.checked)}
            data-testid="promo-show-qr"
            className="w-4 h-4 accent-yellow-500"
          />
          <span className="text-sm text-gray-900">QR-Code auf Desktop anzeigen</span>
        </label>
      </div>

      {/* Targeting */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 space-y-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Targeting & Zeitplan</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Zielgruppe">
            <select
              value={cfg.target_audience}
              onChange={(e) => set('target_audience', e.target.value)}
              data-testid="promo-audience"
              className="w-full bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900"
            >
              <option value="all">Alle Kunden</option>
              <option value="active">Nur aktive Kunden</option>
              <option value="inactive">Nur inaktive Kunden (Reaktivierung)</option>
            </select>
          </Field>
          <Field label="Zeitplan">
            <select
              value={cfg.schedule_mode}
              onChange={(e) => set('schedule_mode', e.target.value)}
              data-testid="promo-schedule"
              className="w-full bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900"
            >
              <option value="always">Immer aktiv</option>
              <option value="scheduled">Zeitraum festlegen</option>
            </select>
          </Field>
        </div>

        {cfg.schedule_mode === 'scheduled' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Start (ISO)">
              <input
                type="datetime-local"
                value={cfg.start_datetime ? cfg.start_datetime.slice(0, 16) : ''}
                onChange={(e) => set('start_datetime', e.target.value ? new Date(e.target.value).toISOString() : '')}
                className="w-full bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900"
              />
            </Field>
            <Field label="Ende (ISO)">
              <input
                type="datetime-local"
                value={cfg.end_datetime ? cfg.end_datetime.slice(0, 16) : ''}
                onChange={(e) => set('end_datetime', e.target.value ? new Date(e.target.value).toISOString() : '')}
                className="w-full bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900"
              />
            </Field>
          </div>
        )}

        <Field label="Kampagnen-ID (für Tracking)">
          <input
            type="text"
            value={cfg.campaign_id || ''}
            onChange={(e) => set('campaign_id', e.target.value)}
            placeholder="z.B. app-launch-2026"
            data-testid="promo-campaign-id"
            className="w-full bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900"
          />
        </Field>
      </div>

      {/* Preview */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Vorschau (Desktop-Ansicht im Portal)</h3>
        <div
          className="rounded-2xl overflow-hidden"
          style={{
            background: `linear-gradient(135deg, ${cfg.background_color} 0%, #1a1a1a 100%)`,
            border: `1px solid ${cfg.primary_color}55`,
          }}
        >
          <div className="flex items-center gap-4 p-5">
            <div
              className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: `${cfg.primary_color}22`, border: `1px solid ${cfg.primary_color}66` }}
            >
              <Smartphone size={22} style={{ color: cfg.primary_color }} />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-white font-bold text-base">{cfg.title || '—'}</h4>
              <p className="text-white/70 text-sm mt-0.5">{cfg.message || '—'}</p>
              <div className="mt-3 flex gap-2">
                {cfg.ios_url && (
                  <span
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold"
                    style={{ background: cfg.primary_color, color: '#000' }}
                  >
                    <Apple size={14} /> App Store
                  </span>
                )}
                {cfg.android_url && (
                  <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold bg-white/10 text-white border border-white/15">
                    <PlayCircle size={14} /> Google Play
                  </span>
                )}
              </div>
            </div>
            {cfg.show_qr && (cfg.ios_url || cfg.android_url) && (
              <div className="p-2 rounded-lg bg-white/5 border border-white/10 flex flex-col items-center gap-1">
                <QRCodeSVG
                  value={cfg.ios_url || cfg.android_url}
                  size={72}
                  bgColor={cfg.background_color}
                  fgColor="#ffffff"
                  level="M"
                  includeMargin={false}
                />
                <span className="text-[10px] text-white/60 uppercase tracking-wider">Scannen</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Save */}
      <div className="flex items-center gap-3">
        <button
          onClick={save}
          disabled={saving}
          data-testid="promo-save-btn"
          className="inline-flex items-center gap-2 px-6 py-2.5 bg-yellow-500 hover:bg-yellow-600 text-black font-bold rounded-lg transition-colors disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save size={16} />}
          Speichern
        </button>
        {cfg.ios_url && (
          <a
            href={cfg.ios_url}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-gray-500 hover:text-yellow-600 inline-flex items-center gap-1"
          >
            iOS testen <ExternalLink size={10} />
          </a>
        )}
        {cfg.android_url && (
          <a
            href={cfg.android_url}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-gray-500 hover:text-yellow-600 inline-flex items-center gap-1"
          >
            Android testen <ExternalLink size={10} />
          </a>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs text-gray-500 mb-1 block">{label}</span>
    {children}
  </label>
);

const StatCard = ({ value, label, color }) => (
  <div className="bg-gray-100 border border-gray-200 rounded-lg p-3">
    <div className="text-xl font-bold" style={{ color }}>{value}</div>
    <div className="text-[10px] text-gray-500 uppercase tracking-wider mt-1">{label}</div>
  </div>
);

export default MobilePromoTab;
